import * as THREE from 'three'

import { FireEffectHandle } from './fireEffect'
import { SparkleEffectHandle } from './sparkleEffect'
import { createStudTexture } from './materialSpecs'
import {
  CORNER_WEDGE_TRIANGLE_POSITIONS,
  WEDGE_TRIANGLE_POSITIONS,
} from '../shapes'

import { RobloxMaterial, ShapeKind, Vec3 } from '../types'

/** BoxGeometry の face groups インデックス: +Y(top) は 2 番目。 */
const TOP_FACE_INDEX = 2

/**
 * 1 オブジェクトに対応する three リソースの束。
 *
 * `container` を transform / raycast / ギズモ装着の対象とし、視覚要素（箱 or
 * 読み込んだモデル）はその子として保持する。これにより、box の scale=size を
 * モデルにも同様に効かせつつ、transform 反映点を 1 箇所に集約できる。
 */
export interface MeshEntry {
  readonly container: THREE.Group
  /** この entry の形状種別。applyState で球/円柱の断面補正に使う。 */
  readonly shape: ShapeKind
  /** 円柱の軸（'x'=Roblox Cylinder パーツ / 'y'=CylinderMesh 由来）。断面補正に使う。 */
  readonly cylinderAxis: 'x' | 'y'
  readonly box: THREE.Mesh
  /** 形状ジオメトリ（box は BoxGeometry、その他は球/円柱/手組み BufferGeometry）。 */
  readonly boxGeometry: THREE.BufferGeometry
  /**
   * 箱の 6 面マテリアル（BoxGeometry の face groups 順: +X, -X, +Y, -Y, +Z, -Z）。
   * 上面（index 2）にはスタッドテクスチャを適用する。
   */
  readonly boxMaterials: THREE.MeshStandardMaterial[]
  /** 上面マテリアルへの参照（boxMaterials[2] と同じ）。 */
  readonly topMaterial: THREE.MeshStandardMaterial
  /** 上面以外のサイドマテリアル群（boxMaterials から上面を除いた 5 枚）。 */
  readonly sideMaterials: THREE.MeshStandardMaterial[]
  /**
   * 上面用スタッドテクスチャ。GLB バックの entry では使わないため null。
   * 非 null の場合 entry ごとの専有インスタンスで、repeat はこの entry の size に従う。
   */
  readonly studTexture: THREE.CanvasTexture | null
  /** 読み込んだ GLTF シーン（未読込・失敗時は undefined）。 */
  model?: THREE.Group
  /** モデル配下のマテリアル集合（model 読込時に 1 度だけ収集して再 traverse を避ける）。 */
  modelMaterials: Set<THREE.Material>
  /** 面に貼った Decal/Texture テクスチャ（dispose で解放するために保持）。 */
  decalTextures: THREE.Texture[]
  /**
   * デカール用オーバーレイマテリアル（白・map 付き）。下地の Part 色を保ったまま面に
   * 画像を重ねるため box 面マテリアルとは別に持つ。オクルージョンフェード/解放で参照する。
   */
  decalMaterials: THREE.MeshStandardMaterial[]
  /**
   * decal タイル繰り返しを UV へ織り込んだ面ごとのオーバーレイジオメトリ（dispose で
   * 解放）。テクスチャを URL 単位で共有するため repeat を texture から外し、面ごとに
   * スケールした UV を持つジオメトリで表現する。繰り返し無し（共有平面 DECAL_PLANE_GEOMETRY）
   * の面では積まれない。
   */
  decalGeometries: THREE.BufferGeometry[]
  /** モデル読込の世代。dispose 後の遅延コールバック適用を防ぐ。 */
  modelToken: number
  /** color/transparency/material の前回反映キー。同値再適用を短絡するための比較用。 */
  visualKey?: string
  /** size(x,z) の前回反映キー。スタッド repeat の更新を size 差分でだけ走らせる。 */
  sizeKey?: string
  /** 炎エフェクト（state.fire=true の間だけ生成・更新される）。 */
  fireEffect?: FireEffectHandle
  /** キラキラエフェクト（state.sparkle=true の間だけ生成・更新される）。 */
  sparkleEffect?: SparkleEffectHandle
  /** sparkleEffect を生成した時点の size キー。寸法変更で発生範囲を作り直す。 */
  sparkleSizeKey?: string
  /**
   * descriptor にデカール定義があるか（同期確定値）。デカール画像のロードは
   * 非同期（decalMaterials は後から増える）ため、グローシェル除外の判定には
   * こちらを使う（例: SpawnLocation は Neon 素材 + 天面デカールの台）。
   */
  hasDecals?: boolean
  /** Neon の発光の滲み（多層グローシェル）。白系発光素材の間だけ存在。 */
  glowGroup?: THREE.Group
  glowMaterials?: THREE.MeshBasicMaterial[]
  /**
   * 「素材をかえる」の模様テクスチャ（無地マテリアルでは undefined）。
   * 画像はマテリアル間で共有、Texture はエントリ固有（repeat が size 依存）。
   */
  materialTexture?: { material: RobloxMaterial; texture: THREE.CanvasTexture }
  /**
   * 天面用の模様テクスチャ（TOP_PATTERN_MATERIALS の素材のみ）。側面用
   * （materialTexture）とは repeat が異なる（天面は size.x/z 基準）ため別に持つ。
   */
  topPatternTexture?: {
    material: RobloxMaterial
    texture: THREE.CanvasTexture
  }
}

export const toVector3 = (vec: Vec3): THREE.Vector3 =>
  new THREE.Vector3(vec.x, vec.y, vec.z)

// 最終姿勢 = Y ヨー(rotationY) ∘ 基準向き(orientation) を計算する再利用テンポラリ。
// 毎フレーム多数オブジェクトで呼ばれるため、確保を避けてモジュール共有で使い回す。
const _yawQuat = new THREE.Quaternion()
const _baseQuat = new THREE.Quaternion()
const _yAxis = new THREE.Vector3(0, 1, 0)
export const composeOrientation = (
  out: THREE.Quaternion,
  rotationY: number,
  base: { x: number; y: number; z: number; w: number },
): THREE.Quaternion => {
  _yawQuat.setFromAxisAngle(_yAxis, rotationY)
  _baseQuat.set(base.x, base.y, base.z, base.w)
  // Qy ∘ base（先に基準向きへ、その後ワールド Y で旋回）。
  return out.copy(_yawQuat).multiply(_baseQuat)
}

/**
 * オブジェクトの視覚・transform を担う 1 エントリを構築する。
 * Roblox 同様 Y-up・座標そのまま。
 *
 * box ジオメトリは単位ボックス（1×1×1）で生成し、寸法は container の scale で
 * 表現する。これにより `ObjectRuntimeState.size` の変化をジオメトリ再生成なしに
 * 反映でき、共有型契約（毎フレーム描画へ反映）に追従できる。
 */
/**
 * 1 エントリ分の 6 面マテリアル＋スタッドテクスチャを生成する。
 * - withStuds=true: 上面にスタッドテクスチャを貼る（通常 box 表示用）。
 * - withStuds=false: GLB バック用のプレースホルダ。スタッド貼付なし（GLB ロード成功時に
 *   box 自体が scene graph から外されるが、ロード失敗時のフォールバックも考慮）。
 */
/**
 * 円柱の軸を size から推定する。
 *
 * Roblox の Cylinder パーツは軸=X（断面 YZ が直径＝y≈z）。一方 CylinderMesh や
 * SpecialMesh の Cylinder は軸=Y（断面 XZ＝x≈z）で縦ポール等に使われる。コンバータが
 * 両者を同じ shape="cylinder" に落とすため、断面（直径を成す等しい2辺）でない「長さ」
 * 方向を軸とみなす: x≈z かつ y が異なれば軸=Y、それ以外は軸=X（既定）。
 */
export const cylinderAxisOf = (size: {
  x: number
  y: number
  z: number
}): 'x' | 'y' => {
  const approxEq = (a: number, b: number): boolean =>
    Math.abs(a - b) <= 1e-3 * Math.max(1, Math.abs(a), Math.abs(b))
  if (approxEq(size.x, size.z) && !approxEq(size.x, size.y)) {
    return 'y'
  }
  return 'x'
}

/**
 * 非インデックス三角形列に対し、各頂点の法線の主軸で平面投影した UV を作る。
 * 単位形状（[-0.5,0.5]^3）前提で 0..1 に正規化する（box の面 UV と同等スケール）。
 * wedge / cornerWedge は手組みジオメトリで UV を持たないため、模様テクスチャ
 * （MeshStandardMaterial.map）を貼る際にこの UV を与える。
 */
const computePlanarUvs = (
  positions: readonly number[],
  normalAttribute: THREE.BufferAttribute | THREE.InterleavedBufferAttribute,
): number[] => {
  const uvs: number[] = []
  for (let i = 0; i < positions.length / 3; i += 1) {
    const x = positions[i * 3]
    const y = positions[i * 3 + 1]
    const z = positions[i * 3 + 2]
    const nx = Math.abs(normalAttribute.getX(i))
    const ny = Math.abs(normalAttribute.getY(i))
    const nz = Math.abs(normalAttribute.getZ(i))
    if (nx >= ny && nx >= nz) {
      uvs.push(z + 0.5, y + 0.5) // ±X 面: ZY 平面投影
    } else if (ny >= nx && ny >= nz) {
      uvs.push(x + 0.5, z + 0.5) // ±Y 面（斜面含む）: XZ 平面投影
    } else {
      uvs.push(x + 0.5, y + 0.5) // ±Z 面: XY 平面投影
    }
  }
  return uvs
}

/** box 以外の単位ジオメトリ。Part.Size は container.scale で乗算するので単位寸法で作る。 */
const buildShapeGeometry = (
  shape: ShapeKind,
  cylinderAxis: 'x' | 'y',
): THREE.BufferGeometry => {
  switch (shape) {
    case 'ball':
      // 直径 1（半径 0.5）。size を直径として等方スケールする。
      return new THREE.SphereGeometry(0.5, 24, 16)
    case 'cylinder': {
      // three の CylinderGeometry は軸=Y。Roblox Cylinder パーツ（軸=X）は Z 周りに
      // 90° 回す。CylinderMesh 由来（軸=Y）はそのまま使う。
      const geometry = new THREE.CylinderGeometry(0.5, 0.5, 1, 24)
      if (cylinderAxis === 'x') {
        geometry.rotateZ(Math.PI / 2)
      }
      return geometry
    }
    case 'wedge':
    case 'cornerWedge': {
      const positions =
        shape === 'wedge' ?
          WEDGE_TRIANGLE_POSITIONS
        : CORNER_WEDGE_TRIANGLE_POSITIONS
      const geometry = new THREE.BufferGeometry()
      geometry.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(Array.from(positions), 3),
      )
      geometry.computeVertexNormals()
      // 模様テクスチャ（「素材をかえる」）用の UV。UV 属性が無いと map 付き
      // マテリアルでシェーダが未定義の uv を読み、格子状のノイズになる。
      // 面の法線の主軸で平面投影する（box の面 UV と同じ 0..1 スケール）。
      geometry.setAttribute(
        'uv',
        new THREE.Float32BufferAttribute(
          computePlanarUvs(positions, geometry.getAttribute('normal')),
          2,
        ),
      )
      return geometry
    }
    case 'box':
    default:
      return new THREE.BoxGeometry(1, 1, 1)
  }
}

export const createMeshEntry = (
  shape: ShapeKind,
  withStuds: boolean,
  cylinderAxis: 'x' | 'y' = 'x',
): MeshEntry => {
  const container = new THREE.Group()
  // box は 6 面マテリアル＋上面スタッドで Roblox らしい見た目にする。
  if (shape === 'box') {
    const boxGeometry = new THREE.BoxGeometry(1, 1, 1)
    const boxMaterials: THREE.MeshStandardMaterial[] = []
    for (let i = 0; i < 6; i += 1) {
      boxMaterials.push(new THREE.MeshStandardMaterial())
    }
    const topMaterial = boxMaterials[TOP_FACE_INDEX]
    const sideMaterials = boxMaterials.filter((_, i) => i !== TOP_FACE_INDEX)
    const studTexture = withStuds ? createStudTexture() : null
    if (studTexture) {
      topMaterial.map = studTexture
    }
    const box = new THREE.Mesh(boxGeometry, boxMaterials)
    container.add(box)
    return {
      container,
      shape,
      cylinderAxis,
      box,
      boxGeometry,
      boxMaterials,
      topMaterial,
      sideMaterials,
      studTexture,
      modelMaterials: new Set<THREE.Material>(),
      decalTextures: [],
      decalMaterials: [],
      decalGeometries: [],
      modelToken: 0,
    }
  }
  // ball/cylinder/wedge/cornerWedge: 単一マテリアル・スタッド無し。
  const geometry = buildShapeGeometry(shape, cylinderAxis)
  const material = new THREE.MeshStandardMaterial()
  // 手組みの wedge/cornerWedge は巻き順が不定なため両面表示で暗転を防ぐ。
  if (shape === 'wedge' || shape === 'cornerWedge') {
    material.side = THREE.DoubleSide
  }
  const box = new THREE.Mesh(geometry, material)
  container.add(box)
  return {
    container,
    shape,
    cylinderAxis,
    box,
    boxGeometry: geometry,
    boxMaterials: [material],
    topMaterial: material,
    sideMaterials: [],
    studTexture: null,
    modelMaterials: new Set<THREE.Material>(),
    decalTextures: [],
    decalMaterials: [],
    decalGeometries: [],
    modelToken: 0,
  }
}

/**
 * texture.image が ImageBitmap なら close() する（GPU 外の CPU 側画像解放）。
 * GLTF は clone 等で同一 ImageBitmap を複数 texture が共有し得るため、
 * closed セットで重複 close を防ぐ。
 */
const closeImageBitmap = (value: unknown, closed: Set<ImageBitmap>): void => {
  if (
    typeof ImageBitmap !== 'undefined' &&
    value instanceof ImageBitmap &&
    !closed.has(value)
  ) {
    closed.add(value)
    value.close()
  }
}

/**
 * material（および uniforms 等のネスト）から Texture を再帰的に収集する。
 * 循環参照・重複は seen / textures で防ぐ。
 */
const collectTextures = (
  value: unknown,
  textures: Set<THREE.Texture>,
  seen: Set<object>,
): void => {
  if (value instanceof THREE.Texture) {
    textures.add(value)
    return
  }
  if (!value || typeof value !== 'object' || seen.has(value)) {
    return
  }
  seen.add(value)
  for (const nested of Object.values(value)) {
    collectTextures(nested, textures, seen)
  }
}

/**
 * 読み込んだ GLTF シーンの geometry / material / texture / skeleton を
 * 再帰的に解放する。Mesh / Points / Line / SkinnedMesh など geometry・material
 * を持つ Object3D を対象にし、共有リソースは Set で重複排除してから dispose する。
 * texture は uniforms 等のネストも辿り、ImageBitmap は close() で解放する。
 */
export const disposeModel = (model: THREE.Object3D): void => {
  const geometries = new Set<THREE.BufferGeometry>()
  const materials = new Set<THREE.Material>()
  const textures = new Set<THREE.Texture>()

  model.traverse((child) => {
    if (child instanceof THREE.SkinnedMesh && child.skeleton) {
      child.skeleton.dispose()
    }
    if (
      child instanceof THREE.Mesh ||
      child instanceof THREE.Points ||
      child instanceof THREE.Line
    ) {
      geometries.add(child.geometry)
      const mats =
        Array.isArray(child.material) ? child.material : [child.material]
      for (const mat of mats) {
        materials.add(mat)
      }
    }
  })

  const seen = new Set<object>()
  for (const material of materials) {
    collectTextures(material, textures, seen)
  }
  for (const geometry of geometries) {
    geometry.dispose()
  }
  const closedImages = new Set<ImageBitmap>()
  for (const texture of textures) {
    // URL 単位で共有しているメッシュテクスチャは個別解放しない（他部品が参照中。
    // 破棄はレンダラの共有キャッシュ dispose に一本化する）。
    if (texture.userData.shared === true) {
      continue
    }
    closeImageBitmap(texture.image, closedImages)
    texture.dispose()
  }
  for (const material of materials) {
    material.dispose()
  }
}

/** メッシュ束の three リソースを解放する（モデル含む）。 */
export const disposeMeshEntry = (entry: MeshEntry): void => {
  // 進行中の非同期ロード（モデル / Roblox mesh / デカール）の遅延コールバックを無効化する。
  // removeEntry は事前に進めるが、dispose（全体破棄）から直接呼ばれる経路もあるため
  // ここでも進めて、unmount 後にロード完了したテクスチャを破棄済みマテリアルへ差さない。
  entry.modelToken += 1
  if (entry.model) {
    disposeModel(entry.model)
  }
  entry.boxGeometry.dispose()
  for (const material of entry.boxMaterials) {
    material.dispose()
  }
  if (entry.studTexture) {
    entry.studTexture.dispose()
  }
  for (const material of entry.decalMaterials) {
    material.dispose()
  }
  // decalTextures は非共有テクスチャ（テキスト看板の CanvasTexture 等）のみを保持する。
  // URL 共有する画像 decal の Texture はここには積まず、レンダラの共有キャッシュが解放する。
  for (const texture of entry.decalTextures) {
    texture.dispose()
  }
  for (const geometry of entry.decalGeometries) {
    geometry.dispose()
  }
  if (entry.fireEffect) {
    entry.fireEffect.dispose()
    entry.fireEffect = undefined
  }
  if (entry.sparkleEffect) {
    entry.sparkleEffect.dispose()
    entry.sparkleEffect = undefined
  }
  if (entry.materialTexture) {
    entry.materialTexture.texture.dispose()
    entry.materialTexture = undefined
  }
  if (entry.topPatternTexture) {
    entry.topPatternTexture.texture.dispose()
    entry.topPatternTexture = undefined
  }
  if (entry.glowMaterials) {
    for (const material of entry.glowMaterials) {
      material.dispose()
    }
    entry.glowMaterials = undefined
    entry.glowGroup = undefined
  }
}
